/**
 * This shared utility is in this place since for Supabase Functions
 * does not allow dependencies to be outside.
 *
 * To make them fully independent, I just write vanilla types here for
 * database models
 */
export type MemberType = "adult" | "junior" | "baby";

export type Season = {
  cost_kid: number;
  cost_adult: number;
  name: string;
  // Birth year thresholds
  adult_start_year: number; // Born in this year or earlier = adults
  junior_start_year: number; // Born after adult_start_year up to this year = juniors, after this year = kids
};

export type Member = {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  subscriptions?: Subscription[];
};

export type Subscription = {
  season_name: string;
  created_at: string;
};

export const MemberUtils = {
  calculateAge: (birthDate: string): number => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();

    // Adjust age if birthday hasn't occurred this year
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() &&
        today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  },

  getBirthYear: (birthDate: string): number => {
    const birth = new Date(birthDate);
    return birth.getFullYear();
  },

  getMemberType: (birthDate: string, currentSeason: Season): MemberType => {
    const birthYear = MemberUtils.getBirthYear(birthDate);

    // Born in adult_start_year or earlier = adults
    if (birthYear <= currentSeason.adult_start_year) {
      return "adult";
    }

    // Born after adult_start_year up to junior_start_year = juniors
    if (birthYear <= currentSeason.junior_start_year) {
      return "junior";
    }

    // Born after junior_start_year = kids
    return "baby";
  },

  getValidSubscription: (
    member: Member,
    currentSeason: Season,
  ): Subscription | null => {
    const validSubscription = member.subscriptions?.find(
      (subscription) => subscription.season_name === currentSeason.name,
    );

    return validSubscription || null;
  },

  hasValidSubscription: (members: Member[], currentSeason: Season): boolean => {
    return members.some((member) => {
      return MemberUtils.getValidSubscription(member, currentSeason) !== null;
    });
  },

  getLastSubscription: (member: Member): Subscription | null => {
    const sortedSubscriptions = member.subscriptions?.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB.getTime() - dateA.getTime();
    });

    if (!sortedSubscriptions) {
      return null;
    }

    return sortedSubscriptions.length > 0 ? sortedSubscriptions[0] : null;
  },

  getMemberPrice: (member: Member, season: Season): number => {
    if (!member.date_of_birth) {
      return 0;
    }
    const memberType = MemberUtils.getMemberType(member.date_of_birth, season);
    switch (memberType) {
      case "baby":
        return season.cost_kid;
      case "junior":
        return season.cost_kid;
      case "adult":
        return season.cost_adult;
    }
  },

  // TODO: metti questo shared insieme all'altro frontend
  getMemberTypeLabel: (type: MemberType) => {
    switch (type) {
      case "adult":
        return "Adulto";
      case "junior":
        return "Junior";
      case "baby":
        return "Bambino";
      default:
        return "Adulto"; // fallback to adult label
    }
  },
};
